import {
  ALL_MODULES,
  type ModuleKey,
  PasswordSchema,
  type ShopScope,
  type SubAccount,
} from "@adpilot/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ModulePermissionTree } from "@/components/account/module-permission-tree";
import { ShopScopePicker } from "@/components/account/shop-scope-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { createSubAccount, updateSubAccount } from "@/lib/account-api";
import { getApiErrorMessage } from "@/lib/api-error";

export const SUB_ACCOUNTS_QUERY_KEY = ["account", "sub-accounts"] as const;

interface SubAccountDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → create mode; a sub-account → edit mode (email/password immutable). */
  editing: SubAccount | null;
}

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
  permissions?: string;
  resources?: string;
}

/**
 * 新建/编辑子账号抽屉(ADP-167,Xnurta 对齐):
 * 姓名/邮箱/密码(仅新建)/权限树/店铺范围;确定时逐字段必填校验。
 */
export function SubAccountDrawer({ open, onOpenChange, editing }: SubAccountDrawerProps) {
  const { t } = useTranslation("account");
  const queryClient = useQueryClient();
  const isEdit = editing !== null;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [modules, setModules] = useState<ModuleKey[]>([]);
  const [scope, setScope] = useState<ShopScope>({ mode: "all" });
  const [errors, setErrors] = useState<FieldErrors>({});

  // Hydrate on open: edit mode pre-fills; create mode resets.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setPassword("");
    if (editing) {
      setName(editing.name ?? "");
      setEmail(editing.email);
      // NOTE: hydrating null (= follow-all) into an explicit list freezes the
      // grant at today's modules once saved — acceptable until a 6th module
      // ships; keep ALL_MODULES as the single source of that list.
      setModules(editing.modulePermissions ?? [...ALL_MODULES]);
      setScope(editing.shopScope ?? { mode: "all" });
    } else {
      setName("");
      setEmail("");
      setModules([]);
      setScope({ mode: "all" });
    }
  }, [open, editing]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        await updateSubAccount(editing.userId, {
          name: name.trim(),
          modulePermissions: modules,
          shopScope: scope,
        });
      } else {
        await createSubAccount({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
          modulePermissions: modules,
          shopScope: scope,
        });
      }
    },
    onSuccess: async () => {
      toast.success(isEdit ? t("drawer.updated") : t("drawer.created"));
      await queryClient.invalidateQueries({ queryKey: SUB_ACCOUNTS_QUERY_KEY });
      onOpenChange(false);
    },
    onError: async (error) => {
      toast.error(await getApiErrorMessage(error));
    },
  });

  function validate(): boolean {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = t("drawer.nameRequired");
    if (!isEdit) {
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) next.email = t("drawer.emailRequired");
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail))
        next.email = t("drawer.emailInvalid");
      if (!password) next.password = t("drawer.passwordRequired");
      else if (!PasswordSchema.safeParse(password).success)
        next.password = t("drawer.passwordPolicy");
    }
    if (modules.length === 0) next.permissions = t("drawer.permissionsRequired");
    if (scope.mode === "selected" && scope.shopIds.length === 0)
      next.resources = t("drawer.resourcesRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleConfirm() {
    if (!validate() || mutation.isPending) return;
    mutation.mutate();
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{isEdit ? t("drawer.editTitle") : t("drawer.createTitle")}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sub-name">
                <span className="text-destructive-foreground">*</span> {t("drawer.name")}
              </Label>
              <Input
                id="sub-name"
                value={name}
                placeholder={t("drawer.placeholder")}
                onChange={(event) => setName(event.target.value)}
                aria-invalid={Boolean(errors.name)}
              />
              {errors.name ? (
                <p className="text-xs text-destructive-foreground">{errors.name}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sub-email">
                <span className="text-destructive-foreground">*</span> {t("drawer.email")}
              </Label>
              <Input
                id="sub-email"
                type="email"
                value={email}
                placeholder={t("drawer.placeholder")}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isEdit}
                aria-invalid={Boolean(errors.email)}
              />
              {errors.email ? (
                <p className="text-xs text-destructive-foreground">{errors.email}</p>
              ) : null}
            </div>
          </div>

          {!isEdit ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sub-password">
                <span className="text-destructive-foreground">*</span> {t("drawer.password")}
              </Label>
              <Input
                id="sub-password"
                type="password"
                value={password}
                placeholder={t("drawer.placeholder")}
                autoComplete="new-password"
                onChange={(event) => setPassword(event.target.value)}
                aria-invalid={Boolean(errors.password)}
              />
              {errors.password ? (
                <p className="text-xs text-destructive-foreground">{errors.password}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{t("drawer.passwordPolicy")}</p>
              )}
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label>
              <span className="text-destructive-foreground">*</span> {t("drawer.permissions")}
            </Label>
            <ModulePermissionTree
              value={modules}
              onChange={(next) => {
                setModules(next);
                if (next.length > 0) setErrors((prev) => ({ ...prev, permissions: undefined }));
              }}
              error={errors.permissions}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>
              <span className="text-destructive-foreground">*</span> {t("drawer.resources")}
            </Label>
            <ShopScopePicker
              value={scope}
              onChange={(next) => {
                setScope(next);
                if (next.mode === "all" || next.shopIds.length > 0)
                  setErrors((prev) => ({ ...prev, resources: undefined }));
              }}
              error={errors.resources}
            />
          </div>
        </div>
        <SheetFooter className="flex-row justify-end gap-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("drawer.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={mutation.isPending}>
            {t("drawer.confirm")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
